// Coordinator — Unified Calendar (Column-per-Doctor Grid)
// Shows all doctors' appointments for a selected date in a column layout

import { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Clock, User, X } from 'lucide-react';

interface Doctor {
    id: string;
    firstName: string;
    lastName: string;
    specialty: string | null;
    calendarColor: string;
    appointments: CalendarAppointment[];
    blocks: CalendarBlock[];
}

interface CalendarAppointment {
    id: string;
    scheduledStart: string;
    scheduledEnd: string;
    status: string;
    serviceType: string;
    appointmentType: string;
    chiefComplaint?: string;
    patient: { id: string; firstName: string; lastName: string };
}

interface CalendarBlock {
    id: string;
    blockStart: string;
    blockEnd: string;
    reason: string | null;
    isFullDay: boolean;
}

import { API_BASE } from '../config/api';

const STATUS_COLORS: Record<string, string> = {
    confirmed: '#16A34A',
    booked: '#F59E0B',
    arrived: '#3B82F6',
    in_progress: '#6366F1',
    completed: '#6B7280',
    cancelled: '#EF4444',
    no_show: '#DC2626',
};

const STATUS_LABELS: Record<string, string> = {
    confirmed: 'Confirmada',
    booked: 'Agendada',
    arrived: 'Llegó',
    in_progress: 'En consulta',
    completed: 'Completada',
    cancelled: 'Cancelada',
    no_show: 'No asistió',
};

// Generate 30-min time rows from 7:00 to 20:00
function generateTimeRows(): string[] {
    const rows: string[] = [];
    for (let h = 7; h < 20; h++) {
        rows.push(`${String(h).padStart(2, '0')}:00`);
        rows.push(`${String(h).padStart(2, '0')}:30`);
    }
    rows.push('20:00');
    return rows;
}

const TIME_ROWS = generateTimeRows();

export function UnifiedCalendarPage() {
    const [doctors, setDoctors] = useState<Doctor[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedAppt, setSelectedAppt] = useState<CalendarAppointment | null>(null);

    const dateStr = currentDate.toISOString().split('T')[0];

    useEffect(() => {
        loadCalendar();
    }, [dateStr]);

    async function loadCalendar() {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/appointments/unified?date=${dateStr}`);
            const json = await res.json();
            setDoctors(json.data || []);
        } catch {
            setDoctors([]);
        } finally {
            setLoading(false);
        }
    }

    function navigateDay(delta: number) {
        const d = new Date(currentDate);
        d.setDate(d.getDate() + delta);
        setCurrentDate(d);
    }

    function getAppointmentsForSlot(doc: Doctor, timeStr: string): CalendarAppointment[] {
        return doc.appointments.filter(a => {
            const start = new Date(a.scheduledStart);
            const slotTime = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`;
            return slotTime === timeStr;
        });
    }

    function isBlockedSlot(doc: Doctor, timeStr: string): CalendarBlock | null {
        const [h, m] = timeStr.split(':').map(Number);
        const slotStart = new Date(currentDate);
        slotStart.setHours(h, m, 0, 0);
        const slotEnd = new Date(slotStart.getTime() + 30 * 60000);

        return doc.blocks.find(b => {
            const bStart = new Date(b.blockStart);
            const bEnd = new Date(b.blockEnd);
            return slotStart < bEnd && slotEnd > bStart;
        }) || null;
    }

    return (
        <div className="fade-in">
            <h1 className="page-title">
                <Calendar size={22} style={{ display: 'inline', marginRight: 8 }} />
                Calendario Unificado
            </h1>

            {/* Date Navigator */}
            <div className="card" style={{ marginBottom: 16, padding: '12px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
                    <button onClick={() => navigateDay(-1)} className="btn btn-icon" style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', padding: 6 }}>
                        <ChevronLeft size={18} />
                    </button>
                    <div style={{ textAlign: 'center', minWidth: 240 }}>
                        <div style={{ fontSize: 16, fontWeight: 700, textTransform: 'capitalize' }}>
                            {currentDate.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                            {doctors.reduce((sum, d) => sum + d.appointments.length, 0)} citas · {doctors.length} médicos
                        </div>
                    </div>
                    <button onClick={() => navigateDay(1)} className="btn btn-icon" style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', padding: 6 }}>
                        <ChevronRight size={18} />
                    </button>
                    <button onClick={() => setCurrentDate(new Date())} style={{ background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                        Hoy
                    </button>
                </div>
            </div>

            {/* Calendar Grid */}
            {loading ? (
                <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--text-3)' }}>
                    <Clock size={32} style={{ marginBottom: 8, opacity: 0.5 }} />
                    <p>Cargando calendario...</p>
                </div>
            ) : doctors.length === 0 ? (
                <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--text-3)' }}>
                    <Calendar size={32} style={{ marginBottom: 8, opacity: 0.5 }} />
                    <p>No hay médicos activos</p>
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: `80px repeat(${doctors.length}, 1fr)`,
                        minWidth: 80 + doctors.length * 180,
                        border: '1px solid var(--border)',
                        borderRadius: 10,
                        overflow: 'hidden',
                        background: 'var(--bg-card)',
                    }}>
                        {/* Header Row */}
                        <div style={{ padding: '12px 8px', background: 'var(--bg-2)', borderBottom: '2px solid var(--border)', fontWeight: 700, fontSize: 12, color: 'var(--text-3)', textAlign: 'center' }}>
                            Hora
                        </div>
                        {doctors.map(doc => (
                            <div key={doc.id} style={{
                                padding: '10px 12px', background: 'var(--bg-2)',
                                borderBottom: '2px solid var(--border)',
                                borderLeft: '1px solid var(--border)',
                                textAlign: 'center',
                            }}>
                                <div style={{ fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: doc.calendarColor, display: 'inline-block' }} />
                                    {doc.firstName} {doc.lastName}
                                </div>
                                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{doc.specialty || 'General'}</div>
                            </div>
                        ))}

                        {/* Time Rows */}
                        {TIME_ROWS.map(timeStr => (
                            <>
                                <div key={`t-${timeStr}`} style={{
                                    padding: '4px 8px', fontSize: 11, fontWeight: 600,
                                    color: 'var(--text-3)', borderTop: '1px solid var(--border)',
                                    fontVariantNumeric: 'tabular-nums', textAlign: 'center',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    minHeight: 40,
                                }}>
                                    {timeStr}
                                </div>
                                {doctors.map(doc => {
                                    const appts = getAppointmentsForSlot(doc, timeStr);
                                    const block = isBlockedSlot(doc, timeStr);

                                    return (
                                        <div key={`${doc.id}-${timeStr}`} style={{
                                            borderTop: '1px solid var(--border)',
                                            borderLeft: '1px solid var(--border)',
                                            padding: 2, minHeight: 40,
                                            background: block ? 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(107,114,128,0.08) 5px, rgba(107,114,128,0.08) 10px)' : 'transparent',
                                        }}>
                                            {block && appts.length === 0 && (
                                                <div style={{
                                                    fontSize: 10, color: '#6B7280', textAlign: 'center',
                                                    padding: '4px 2px', fontStyle: 'italic',
                                                }}>
                                                    🚫 {block.reason || 'Bloqueado'}
                                                </div>
                                            )}
                                            {appts.map(a => (
                                                <div key={a.id} onClick={() => setSelectedAppt(a)} style={{
                                                    background: `${STATUS_COLORS[a.status] || '#6B7280'}18`,
                                                    borderLeft: `3px solid ${STATUS_COLORS[a.status] || '#6B7280'}`,
                                                    borderRadius: 4, padding: '3px 6px', cursor: 'pointer',
                                                    fontSize: 11, marginBottom: 1,
                                                    transition: 'transform 0.15s',
                                                }}>
                                                    <div style={{ fontWeight: 600, lineHeight: 1.2 }}>
                                                        {a.patient.firstName} {a.patient.lastName.charAt(0)}.
                                                    </div>
                                                    <div style={{ color: 'var(--text-3)', fontSize: 10 }}>
                                                        {STATUS_LABELS[a.status] || a.status}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })}
                            </>
                        ))}
                    </div>
                </div>
            )}

            {/* Appointment Detail Modal */}
            {selectedAppt && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
                }} onClick={() => setSelectedAppt(null)}>
                    <div className="card" style={{ width: 380, padding: 24, maxHeight: '80vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <h3 style={{ margin: 0 }}>Detalle de Cita</h3>
                            <button onClick={() => setSelectedAppt(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)' }}>
                                <X size={20} />
                            </button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: 'var(--text-3)' }}>Paciente</span>
                                <span style={{ fontWeight: 600 }}>{selectedAppt.patient.firstName} {selectedAppt.patient.lastName}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: 'var(--text-3)' }}>Hora</span>
                                <span style={{ fontWeight: 600 }}>{new Date(selectedAppt.scheduledStart).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: 'var(--text-3)' }}>Estado</span>
                                <span className={`badge`} style={{ background: `${STATUS_COLORS[selectedAppt.status]}20`, color: STATUS_COLORS[selectedAppt.status] }}>
                                    {STATUS_LABELS[selectedAppt.status] || selectedAppt.status}
                                </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: 'var(--text-3)' }}>Tipo</span>
                                <span>{selectedAppt.appointmentType === 'telehealth' ? '📹 Teleconsulta' : '🏥 Presencial'}</span>
                            </div>
                            {selectedAppt.chiefComplaint && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: 'var(--text-3)' }}>Motivo</span>
                                    <span>{selectedAppt.chiefComplaint}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
